import { useId, useState } from 'react';
import { plural } from '@/lib/plural';
import { Badge, Checkbox, EmptyState, Skeleton } from '@/components/ui';
import { FieldValue } from '@/components/objects/FieldValue';
import type { FieldRegistry } from '@/components/objects/fieldRegistry';
import { fieldSchemaError, type FieldDefinition } from '@/components/objects/typedFieldData';
import { compareVersions, type ObjectVersion } from './viewData';
export interface VersionComparisonProps {
  label: string;
  definitions: readonly FieldDefinition[];
  before: ObjectVersion;
  after: ObjectVersion;
  beforeLabel?: string;
  afterLabel?: string;
  registry?: FieldRegistry;
  state?: 'ready' | 'loading' | 'error';
  onRetry?: () => void;
}
const labels = {
  added: 'Добавлено',
  removed: 'Удалено',
  changed: 'Изменено',
  same: 'Без изменений',
} as const;
export function VersionComparison({
  label,
  definitions,
  before,
  after,
  beforeLabel = 'Было',
  afterLabel = 'Стало',
  registry,
  state = 'ready',
  onRetry,
}: VersionComparisonProps) {
  const id = useId(),
    [all, setAll] = useState(false);
  const changes = compareVersions(definitions, before, after),
    changed = changes.filter((item) => item.kind !== 'same'),
    shown = all ? changes : changed;
  const schemaError = fieldSchemaError(definitions);
  if (schemaError) return <EmptyState title="Сравнение недоступно" description={schemaError} />;
  if (state === 'error')
    return (
      <EmptyState
        title="Версии не загрузились"
        action={onRetry ? { label: 'Повторить', onClick: onRetry } : undefined}
      />
    );
  return (
    <section data-slot="version-comparison" aria-label={label} className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <p role="status" className="text-sm text-fg-muted">
          {state === 'loading'
            ? 'Сравниваем версии…'
            : changed.length +
              ' ' +
              plural(changed.length, 'изменённое поле', 'изменённых поля', 'изменённых полей')}
        </p>
        <Checkbox
          label="Показать без изменений"
          checked={all}
          disabled={state === 'loading'}
          onCheckedChange={(value) => setAll(value === true)}
        />
      </header>
      {state === 'loading' ? (
        <div aria-busy="true" className="space-y-3">
          {definitions.slice(0, 4).map((definition) => (
            <div key={definition.id} className="grid gap-3 rounded-md border border-line p-4 sm:grid-cols-3">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ))}
        </div>
      ) : !shown.length ? (
        <EmptyState title="Изменений нет" description="Значения в выбранных версиях совпадают." />
      ) : (
        <div className="space-y-3">
          {shown.map(({ definition, kind, before: old, after: next }) => (
            <article
              key={definition.id}
              aria-labelledby={id + definition.id}
              data-change-kind={kind}
              className="overflow-hidden rounded-lg border border-line bg-card"
            >
              <header className="flex items-center justify-between gap-3 border-b border-line bg-muted px-4 py-3">
                <h3 id={id + definition.id} className="type-subheading">
                  {definition.label}
                </h3>
                <Badge variant={kind === 'added' ? 'success' : kind === 'removed' ? 'warn' : 'neutral'}>
                  {labels[kind]}
                </Badge>
              </header>
              <div className="grid sm:grid-cols-2">
                <div className="min-w-0 border-b border-line p-4 sm:border-r sm:border-b-0">
                  <p className="mb-2 text-xs font-medium text-fg-muted">{beforeLabel}</p>
                  {old ? (
                    <FieldValue definition={definition} value={old} registry={registry} />
                  ) : (
                    <span className="text-sm text-fg-subtle">Поля не было</span>
                  )}
                </div>
                <div className="min-w-0 p-4">
                  <p className="mb-2 text-xs font-medium text-fg-muted">{afterLabel}</p>
                  {next ? (
                    <FieldValue definition={definition} value={next} registry={registry} />
                  ) : (
                    <span className="text-sm text-fg-subtle">
                      {kind === 'removed' ? 'Поле удалено' : 'Поля нет'}
                    </span>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
